import { useEffect, type RefObject } from "react";

/**
 * TECLADO iOS (14-sep-2026): el teclado encoge el viewport VISUAL pero ni
 * `dvh` ni los elementos fixed se enteran (se anclan al de maquetación), y el
 * composer de un chat acaba tapado o con una banda blanca muerta encima del
 * teclado. Este hook, mientras el teclado esté abierto en pantallas
 * estrechas, fija la ALTURA del contenedor a lo realmente visible
 * (VisualViewport API) y la restaura al cerrarse.
 *
 * Lo usan la página completa de Conversaciones y (con su propia variante
 * fixed) el mini-inbox flotante.
 */
export function useKeyboardAwareHeight(
  ref: RefObject<HTMLElement | null>,
  maxWidthPx = 767,
): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const el = ref.current;
      if (!el) return;
      const isNarrow = window.matchMedia(`(max-width: ${maxWidthPx}px)`).matches;
      const layoutHeight = document.documentElement.clientHeight;
      const hiddenBelow = layoutHeight - vv.height - vv.offsetTop;
      const keyboardOpen = hiddenBelow > 80;
      if (isNarrow && keyboardOpen) {
        // Fondo visible en coordenadas de maquetación − posición actual del
        // contenedor = altura que deja el composer apoyado en el teclado.
        const rectTop = el.getBoundingClientRect().top;
        el.style.height = `${Math.max(vv.offsetTop + vv.height - rectTop, 240)}px`;
      } else {
        el.style.height = "";
      }
    };
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [ref, maxWidthPx]);
}
